import { getConfig } from "@/features/branding";
import { encryptData, encryptSecret, generateRandomSecretKey } from "@/features/cryptography";
import { HacsOmUploadData } from "@/features/hacs";
import { RemoteNewRunModal, RemoteNewRunModalProps } from "@/features/remote_jobs";
import { StoreState } from "@/store";
import { AnaHazardId } from "@/store/services/facades/types";
import { useGetOrgQuery } from "@/store/services/supabase";
import { PortfolioExportIdFactory } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportIdFactory";
import { PortfolioExportErrorAlert } from "@/tools/aggregate/portfolio-export/components/PortfolioExportErrorAlert";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { streamToSupabase } from "@/tools/aggregate/portfolio-export/utils/supabase";
import { validateCsvFile } from "@/tools/aggregate/portfolio-export/utils/validation";
import { LargeSiteScreenUploadReview } from "@/tools/risk-bands/large-site-screen/components/LargeSiteScreenUploadReview";
import { useLssTutorialState } from "@/tools/risk-bands/large-site-screen/context/LssTutorialContext";
import { arrayBufferToString } from "@/utils/crypto";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { Timeline } from "antd";
import { RcFile } from "antd/es/upload";
import type { Polygon } from "leaflet";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

/**
 * Provides an interface for a user to setup and  trigger a new Portfolio Export
 * run.
 */
export const LargeSiteScreenNewRunModal = (props: RemoteNewRunModalProps<PortfolioExportJobSchema>) => {
  const router = useRouter();
  const [data, setData] = useState<HacsOmUploadData | undefined>();
  const {
    defaultMetadata
  } = getConfig();
  const [polygons, setPolygons] = useState<ReturnType<Polygon["toGeoJSON"]>[]>([]);
  const [status, setStatus] = useState<"INITIAL" | "IN_REVIEW" | "UPLOADING">("INITIAL");
  const [riskiestPoint, setRiskiestPoint] = useState<{
    hazard: AnaHazardId | "total";
    year: number;
  }>();
  const [isTutorialHandoff, setIsTutorialHandoff] = useState(false);
  const {
    defaultAdvancedSettingsOpen,
    setDefaultAdvancedSettingsOpen,
    setAdvancedSettingsRefs,
    reviewSection,
    usageSection,
    runAnalysisBtn
  } = useLssTutorialState();
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite: true
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [key] = useKeys();
  const {
    data: org
  } = useGetOrgQuery();
  useEffect(() => {
    if (Object.keys(router.query).includes("handoff")) {
      const data = sessionStorage.getItem("largeSiteScreenHandoffData");
      if (!data) {
        return;
      }
      const {
        assets,
        polygons,
        isTutorial
      }: {
        assets: string;
        polygons: ReturnType<Polygon["toGeoJSON"]>[];
        isTutorial?: boolean;
      } = JSON.parse(data);
      setIsTutorialHandoff(!!isTutorial);
      if (isTutorial) setDefaultAdvancedSettingsOpen(true);
      const file: any = new File([assets], `${polygons[0].properties.name || "Large Site Analysis"} - ${new Date(Date.now()).toLocaleString()}`, {
        type: "text/csv",
        lastModified: Date.now()
      });

      // file["lastModifiedDate"] = new Date();
      // file["uid"] = `rc-upload-${Math.floor(Math.random() * 99999999)}-1`;

      setData({
        file: file as RcFile,
        lineCount: assets.split("\n").length,
        name: "",
        isPrivate: true,
        anomalyManagementRp: true,
        defences: true
      });
      setPolygons(polygons);
      setStatus("IN_REVIEW");
    }
  }, []);
  const validateFile = async (file: RcFile) => {
    if (isTutorialHandoff) return;
    setErrors([]);
    const {
      portfolioExport: config
    } = getConfig();
    const portfolioCount = {
      min: org?.is_large_bank ? config.largeBank.minAddresses : 0,
      max: config.maxAddresses
    };
    const {
      status,
      messages
    } = await validateCsvFile(file, portfolioCount);
    if (status === "error") {
      setErrors(messages);
      setData(undefined);
      setStatus("INITIAL");
      return;
    }
  };
  const uploadFile = async (file: RcFile) => {
    let succeeded = false;
    try {
      let encryptedSecret = "";
      const idFactory = new PortfolioExportIdFactory();
      const [supabaseStream] = file.stream().tee();
      if (!key.user?.keyPair.publicKey) return;
      const newSecret = await generateRandomSecretKey();
      if (data?.isPrivate) {
        encryptedSecret = arrayBufferToString(await encryptSecret({
          publicKey: key.user.keyPair.publicKey,
          secret: newSecret.exportedKey
        }));
      } else {
        if (key.orgSecret && key.org?.keyPair.publicKey) {
          encryptedSecret = arrayBufferToString(await encryptSecret({
            publicKey: key.org.keyPair.publicKey,
            secret: newSecret.exportedKey
          }));
        }
      }
      const portfolioDetails = {
        name: await encryptData(newSecret.secret, data?.name || removeFileExtension(file.name) || ""),
        groupId: data?.groupId,
        startTime: Date.now(),
        encryptedSecret,
        isLargeSite: true,
        isPrivate: data?.isPrivate,
        metadata: await encryptData(newSecret.secret, JSON.stringify({
          riskiestPoint,
          polygons
        })),
        incorporatedHazards: []
      };
      const supabaseResponse = await streamToSupabase({
        stream: supabaseStream,
        idFactory,
        details: portfolioDetails,
        secret: newSecret.secret,
        canRunAutoAdaptation,
        ...{
          defences: data?.defences ? defaultMetadata.defences : defaultMetadata.defencesDisabled
        },
        ...{
          anomalyManagementRp: data?.anomalyManagementRp ? defaultMetadata.anomalyManagementRp : defaultMetadata.anomalyManagementRpDisabled
        }
      });
      if (supabaseResponse.type === "failure") {
        setErrors(["An error occurred while processing your data. This analysis run will not deduct from your quota. Please contact XDI Support with the time/date of your analysis so we can help troubleshoot this error."]);
        return;
      }
      succeeded = true;

      // We just need to alert down the line that we should close the modal.
      if (props.onCancel) {
        props.onCancel({} as any);
      }
    } catch (error) {
      if (/OperationError/.test(String(error))) {
        setErrors(["The file may contain a line that is currently too long to upload.  This is a known limitation that will be resolved in Q1 2025.", "In the meantime, please ensure addresses follow the format recommendation in the Geocoding section of the Methods documentation (available in the Help menu)."]);
        return;
      } else {
        setErrors(["An error occurred while processing your data. This analysis run will not deduct from your quota. Please contact XDI Support with the time/date of your analysis so we can help troubleshoot this error."]);
        return;
      }
    } finally {
      setStatus(succeeded ? "INITIAL" : "IN_REVIEW");
    }
  };
  const totalUploaded = useSelector(({
    portfolioExport
  }: StoreState) => portfolioExport.newRun.totalInSupabase);
  const validatedFile = useRef<RcFile>();
  useEffect(() => {
    if (data) {
      switch (status) {
        case "IN_REVIEW":
          // Re-entered after a failed upload: the file is unchanged and already
          // validated, and re-running would clear the error the user needs to read.
          if (validatedFile.current === data.file) return;
          validatedFile.current = data.file;
          validateFile(data.file);
          return;
        case "UPLOADING":
          uploadFile(data.file);
          return;
      }
    }
  }, [data, status]);
  return <RemoteNewRunModal footer={null} closable={status !== "UPLOADING"} mask={{
    closable: false
  }} title="Review Large Site Analysis" width={560} {...props} data-sentry-element="RemoteNewRunModal" data-sentry-component="LargeSiteScreenNewRunModal" data-sentry-source-file="LargeSiteScreenNewRunModal.tsx">
      <div style={{
      paddingBlockStart: 8
    }}>
        {!!errors.length && <PortfolioExportErrorAlert errors={errors} onClose={() => setErrors([])} />}

        {data && status === "IN_REVIEW" && <LargeSiteScreenUploadReview data={data} setData={setData} onContinue={() => {
        setStatus("UPLOADING");
      }} polygons={polygons} setRiskiestPoint={setRiskiestPoint} defaultAdvancedSettingsOpen={defaultAdvancedSettingsOpen} onAdvancedSettingsRefs={setAdvancedSettingsRefs} refs={{
        reviewSection,
        usageSection,
        runAnalysisBtn
      }} />}

        {data && status === "UPLOADING" && <div style={{
        padding: "48px 32px 20px 32px",
        borderRadius: 8,
        background: "rgba(0, 0, 0, 0.02)",
        border: "1px solid rgba(217, 217, 217, 0.4)"
      }}>
            <Timeline items={[{
          children: `${totalUploaded} encrypted assets uploaded.`
        }]} pending={<strong>Upload in progress. Do not close your browser.</strong>} />
          </div>}
      </div>
    </RemoteNewRunModal>;
};